import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useContent } from '../contexts/ContentContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import QuantityInput from './QuantityInput';
import StepNavigation from './StepNavigation';
import Uppy from '@uppy/core';
import { ProgressBar } from 'primereact/progressbar';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function UploadSection({ onUpload, loading, uploadedCount, activeTab, setActiveTab, onApplyBulkSettings, printSizes, cartItems, uploadedImages }) {
  const { t } = useContent();
  const [dragActive, setDragActive] = useState(false);
  const [uploadSettings, setUploadSettings] = useState(null);
  const [uploaderHelpText, setUploaderHelpText] = useState('Supports: JPG, PNG, HEIC, WebP • Max 10MB per file');
  const [bulkDiscounts, setBulkDiscounts] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState(0);
  const uppyInstance = useRef(null);

  // Initialize Uppy and fetch upload settings
  useEffect(() => {
    const fetchUploadSettings = async () => {
      try {
        const response = await axios.get('/upload-settings');
        setUploadSettings(response.data);
        
        // Generate dynamic help text
        const formats = (response.data.allowed_file_formats || []).map(f => f.toUpperCase()).join(', ');
        const maxSize = response.data.max_file_size_mb || 50;
        const minDpi = response.data.min_dpi || 72;
        const dynamicText = `Supports: ${formats} • Max ${maxSize}MB per file • Min ${minDpi} DPI`;
        setUploaderHelpText(dynamicText);

        // Initialize Uppy with dynamic restrictions
        const allowedTypes = (response.data.allowed_file_formats || []).map(f => `image/${f === 'jpg' ? 'jpeg' : f}`);
        const maxFileSize = (response.data.max_file_size_mb || 50) * 1024 * 1024; // Convert to bytes

        uppyInstance.current = new Uppy({
          restrictions: {
            maxFileSize: maxFileSize,
            allowedFileTypes: allowedTypes.length > 0 ? allowedTypes : ['image/*'],
          },
          autoProceed: false,
        });

        // Handle file selection via Uppy
        uppyInstance.current.on('files-added', (files) => {
          console.log('📂 Uppy files selected:', files.map(f => f.name));
          // Convert Uppy files to native File objects
          const nativeFiles = files.map(file => file.data);
          // Clear Uppy state after extracting files
          uppyInstance.current.cancelAll();
          // Pass to existing upload handler
          onUpload(nativeFiles);
        });

        // Handle file errors
        uppyInstance.current.on('restriction-failed', (file, error) => {
          console.error('Uppy restriction failed:', error);
          toast.error(error.message || 'File upload restriction failed');
        });

      } catch (error) {
        console.error('Failed to fetch upload settings:', error);
      }
    };
    fetchUploadSettings();

    // Cleanup Uppy on unmount
    return () => {
      if (uppyInstance.current) {
        uppyInstance.current.cancelAll();
        uppyInstance.current = null;
      }
    };
  }, [onUpload]);
  
  // Fetch bulk discounts
  useEffect(() => {
    const fetchBulkDiscounts = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/bulk-discounts`);
        const discountMap = {};
        response.data.discounts.forEach(d => {
          discountMap[d.product_sku] = {
            discount_price: d.discount_price,
            discount_quantity: d.discount_quantity
          };
        });
        setBulkDiscounts(discountMap);
        console.log('📊 Bulk discounts loaded in upload:', discountMap);
      } catch (error) {
        console.error('Failed to load bulk discounts:', error);
      }
    };
    fetchBulkDiscounts();
  }, []);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showNextActionDialog, setShowNextActionDialog] = useState(false);
  const [bulkSelections, setBulkSelections] = useState({});
  const [justUploadedCount, setJustUploadedCount] = useState(0);
  const [previousImagesSnapshot, setPreviousImagesSnapshot] = useState([]);
  // Track image count before upload starts to detect new uploads
  const previousImageCount = React.useRef(uploadedImages.length);
  const wasLoading = React.useRef(false);

  // Get featured products from printSizes
  const featuredProducts = Object.entries(printSizes || {}).filter(([, product]) => product.featured);

  // Initialize bulk selections from existing cart items
  useEffect(() => {
    if (cartItems && cartItems.length > 0 && Object.keys(bulkSelections).length === 0) {
      const cartSizesQty = {};
      cartItems.forEach(item => {
        // Use SKU as the key since printSizes is keyed by SKU
        const key = item.sku || item.size;
        if (!cartSizesQty[key]) {
          cartSizesQty[key] = item.quantity;
        }
      });
      setBulkSelections(cartSizesQty);
    }
  }, [cartItems]);

  // Track when on upload tab and save snapshot (but don't reset previousUploadCount)
  useEffect(() => {
    if (activeTab === 'upload') {
      setPreviousImagesSnapshot(uploadedImages);
    }
  }, [activeTab]);

  // Track loading state changes to detect upload completion
  useEffect(() => {
    const newImageCount = uploadedImages.length;
    const newUploadsCount = newImageCount - previousImageCount.current;
    
    // Detect transition from loading=true to loading=false (upload just completed)
    // OR detect when image count increases while not loading (in case batching causes race)
    const uploadJustCompleted = wasLoading.current && !loading;
    const imagesJustIncreased = !loading && newUploadsCount > 0;
    
    if (uploadJustCompleted || imagesJustIncreased) {
      if (newUploadsCount > 0) {
        console.log(`📤 Upload completed: was=${previousImageCount.current}, now=${newImageCount}, new=${newUploadsCount}, trigger=${uploadJustCompleted ? 'loading-change' : 'image-increase'}`);
        
        setJustUploadedCount(newUploadsCount);
        
        // Removed redundant toast - the bulk dialog confirms the upload
        
        // Show bulk selection modal after short delay
        setTimeout(() => {
          setShowBulkDialog(true);
          console.log(`✅ Showing bulk dialog for ${newUploadsCount} new images`);
        }, 500);
        
        // Update the ref to current count
        previousImageCount.current = newImageCount;
      }
    }
    
    // Track loading state
    wasLoading.current = loading;
  }, [loading, uploadedImages.length]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      
      if (uppyInstance.current) {
        try {
          // Add files to Uppy for validation before upload
          files.forEach(file => {
            try {
              uppyInstance.current.addFile({
                name: file.name,
                type: file.type,
                data: file,
              });
            } catch (err) {
              console.error('Failed to add dropped file to Uppy:', err);
            }
          });
        } catch (error) {
          console.error('Drop upload failed:', error);
          toast.error('Failed to upload files');
        }
      } else {
        // Fallback: Direct upload without Uppy validation
        console.log('📂 Uppy not ready, using direct upload for dropped files:', files.map(f => f.name));
        onUpload(files);
      }
    }
  };

  const handleSkip = () => {
    // Skip to edit images tab without applying defaults
    setActiveTab('select');
  };

  const handleApplyBulk = () => {
    // Get only the newly uploaded images (not already in previous snapshot)
    const newImages = uploadedImages.filter(img => 
      !previousImagesSnapshot.find(prev => prev.filename === img.filename)
    );
    
    // Apply each selected size/quantity combination only to new images
    Object.entries(bulkSelections).forEach(([size, quantity]) => {
      if (quantity > 0) {
        onApplyBulkSettings(quantity, size, newImages);
      }
    });
    setShowBulkDialog(false);
    // Update ref so modal doesn't show again until next upload
    previousImageCount.current = uploadedImages.length;
    setShowNextActionDialog(true); // Ask user what they want to do next
  };

  return (
    <div className="space-y-6">
      {/* Step Navigation */}
      <StepNavigation 
        currentStep="upload"
        onStepChange={setActiveTab}
        canGoNext={uploadedCount > 0}
        canGoBack={false}
      />
      
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Upload Your Images</h2>
        <p className="text-gray-600 mb-8">Drag and drop multiple images or click to browse</p>
      </div>

      <Card className="animated-border-element border-2 border-dashed border-gray-300 hover:border-orange-400 transition-colors">
        <CardContent className="p-12">
          <div 
            className={`text-center space-y-4 ${
              dragActive ? 'bg-orange-50 border-orange-300' : ''
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {loading ? (
              <div className="space-y-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600">Processing images...</p>
              </div>
            ) : (
              <>
                <i className="pi pi-cloud-upload text-6xl text-gray-400 mx-auto block"></i>
                <div>
                  <p className="text-xl font-medium text-gray-900 mb-2">Drop images here</p>
                  <p className="text-gray-600 mb-4">or</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const files = Array.from(e.target.files);
                        
                        // If Uppy is available, use it for validation
                        if (uppyInstance.current) {
                          files.forEach(file => {
                            try {
                              uppyInstance.current.addFile({
                                name: file.name,
                                type: file.type,
                                data: file,
                              });
                            } catch (err) {
                              console.error('Failed to add file to Uppy:', err);
                            }
                          });
                        } else {
                          // Fallback: Direct upload without Uppy validation
                          console.log('📂 Uppy not ready, using direct upload for:', files.map(f => f.name));
                          onUpload(files);
                        }
                        // Reset input so same files can be selected again
                        e.target.value = '';
                      }
                    }}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <Button
                    onClick={() => document.getElementById('file-upload').click()}
                    data-testid="browse-files-button"
                  >
                    Browse Files
                  </Button>
                </div>
                <p className="text-sm text-gray-500">
                  {uploaderHelpText}
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Success message now shown at top */}

      {/* Bulk Settings Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent 
          className="w-[90vw] max-w-[90vw] max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              {t('dialog.select_sizes_title', 'Select Print Sizes and Quantities')}
            </DialogTitle>
            <DialogDescription>
              {t('dialog.select_sizes', 'Select print sizes and quantities for your newly uploaded images. You can customize individual images later or upload more images.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Print Sizes (for {justUploadedCount} new {justUploadedCount === 1 ? 'image' : 'images'})
              </label>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                {(featuredProducts.length > 0 ? featuredProducts : Object.entries(printSizes || {})).map(([sku, product]) => {
                  // Use SKU as the key for cart items
                  const currentQty = bulkSelections[sku] || 0;
                  const isSelected = currentQty > 0;
                  const productSku = product.sku || sku;
                  const meetsThreshold = bulkDiscounts[productSku] && currentQty >= bulkDiscounts[productSku].discount_quantity;
                  
                  return (
                    <div 
                      key={sku}
                      className={`flex flex-col p-3 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-custom-orange bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {/* Left side: Name + Price + Discount */}
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm font-semibold block ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                            {product.name}
                          </span>
                          <div className="text-xs text-gray-900 mt-0.5">
                            ${((product.price || 0) / 100).toFixed(2)} each
                          </div>
                          {bulkDiscounts[productSku] && (
                            <div className="mt-0.5">
                              {meetsThreshold ? (
                                <div className="text-xs font-semibold text-green-600 flex items-center gap-1">
                                  <i className="pi pi-check-circle text-green-600"></i>
                                  ${(bulkDiscounts[productSku].discount_price / 100).toFixed(2)} each
                                </div>
                              ) : (
                                <div className="text-xs text-orange-600">
                                  ${(bulkDiscounts[productSku].discount_price / 100).toFixed(2)} when qty ≥ {bulkDiscounts[productSku].discount_quantity}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Right side: Quantity (hidden on mobile, shown on large screens) */}
                        <div className="hidden lg:block">
                          <QuantityInput
                            value={currentQty}
                            onChange={(qty) => {
                              setBulkSelections({ ...bulkSelections, [sku]: qty });
                            }}
                            min={0}
                            max={999}
                          />
                        </div>
                      </div>
                      
                      {/* Quantity below for mobile */}
                      <div className="lg:hidden flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                        <span className="text-sm text-gray-600">Qty:</span>
                        <QuantityInput
                          value={currentQty}
                          onChange={(qty) => {
                            setBulkSelections({ ...bulkSelections, [sku]: qty });
                          }}
                          min={0}
                          max={999}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {featuredProducts.length === 0 && Object.keys(printSizes || {}).length > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  No featured products set. Showing all products.
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowBulkDialog(false);
                previousImageCount.current = uploadedImages.length; // Update so modal doesn't reappear
                // Stay on upload tab to upload more
              }}
            >
              Upload More Images
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowBulkDialog(false);
                  previousImageCount.current = uploadedImages.length; // Update so modal doesn't reappear
                  setActiveTab('select');
                }}
              >
                <i className="pi pi-arrow-right mr-2"></i>
                Skip & Continue
              </Button>
              <Button 
                onClick={handleApplyBulk} 
                className="bg-custom-orange hover:bg-custom-orange"
                disabled={Object.values(bulkSelections).every(qty => qty === 0)}
              >
                <i className="pi pi-check mr-2"></i>
                Apply to All Images
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Next Action Dialog */}
      <Dialog open={showNextActionDialog} onOpenChange={setShowNextActionDialog}>
        <DialogContent 
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              {t('dialog.settings_applied', 'Settings Applied!')}
            </DialogTitle>
            <DialogDescription>
              {t('dialog.settings_applied_desc', 'Your bulk settings have been applied to all newly uploaded images. What would you like to do next?')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowNextActionDialog(false);
                setActiveTab('select');
              }}
              className="flex-1"
            >
              Edit Images
            </Button>
            <Button
              onClick={() => {
                setShowNextActionDialog(false);
                setActiveTab('cart');
              }}
              className="bg-custom-orange hover:bg-custom-orange flex-1"
            >
              Go to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}


export default UploadSection;
